import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { Permission, FeatureId, FEATURES, UserRole } from '../types/permissions';
import { toast } from 'sonner';

interface PermissionContextType {
  userPermissions: Permission[];
  hasPermission: (featureId: FeatureId) => boolean;
  loadingPermissions: boolean;
  refreshPermissions: () => Promise<void>;
  updatePermission: (permissionId: string, canAccess: boolean) => Promise<boolean>;
  createPermission: (role: UserRole, featureId: FeatureId, canAccess: boolean) => Promise<boolean>;
  getDefaultPermissions: (role: UserRole) => Permission[];
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user, isAdmin } = useAuth();
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Load permissions when user changes
  useEffect(() => {
    if (user) {
      loadPermissions();
    } else {
      setUserPermissions([]);
      setLoadingPermissions(false);
    }
  }, [user]);

  // Load permissions from database
  const loadPermissions = async () => {
    try {
      setLoadingPermissions(true);
      
      // If user is admin, they have access to everything
      if (isAdmin) {
        const adminPermissions = FEATURES.map(feature => ({
          id: `admin-${feature.id}`,
          role: 'admin' as UserRole,
          feature_id: feature.id,
          can_access: true
        }));
        setUserPermissions(adminPermissions);
        setLoadingPermissions(false);
        return;
      }
      
      // For non-admin users, fetch their user-specific permissions
      const { data: permissionsData, error: permissionsError } = await supabase
        .from('user_permissions')
        .select('*')
        .eq('user_id', user?.id);
      
      if (permissionsError) throw permissionsError;
      
      // Set the user permissions
      setUserPermissions(permissionsData as Permission[]);
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Fall back to default permissions based on user role
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', user?.id)
        .single();
        
      const userRole = userData?.role as UserRole || 'user';
      const defaultPermissions = getDefaultPermissions(userRole);
      setUserPermissions(defaultPermissions);
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Refresh permissions
  const refreshPermissions = async () => {
    await loadPermissions();
  };

  // Check if user has permission for a feature
  const hasPermission = (featureId: FeatureId): boolean => {
    // Admins always have access to everything
    if (isAdmin) return true;
    
    // Check if user has explicit permission
    const permission = userPermissions.find(p => p.feature_id === featureId);
    if (permission) return permission.can_access;
    
    // If no explicit permission, check default permissions
    return false;
  };

  // Update a permission
  const updatePermission = async (permissionId: string, canAccess: boolean): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('role_permissions')
        .update({ can_access: canAccess })
        .eq('id', permissionId);
        
      if (error) throw error;
      
      // Update local state
      setUserPermissions(prev => 
        prev.map(p => p.id === permissionId ? { ...p, can_access: canAccess } : p)
      );
      
      return true;
    } catch (error) {
      console.error('Error updating permission:', error);
      toast.error('Failed to update permission');
      return false;
    }
  };

  // Create a new permission
  const createPermission = async (role: UserRole, featureId: FeatureId, canAccess: boolean): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('role_permissions')
        .insert([
          { role, feature_id: featureId, can_access: canAccess }
        ])
        .select()
        .single();
        
      if (error) throw error;
      
      // Update local state
      setUserPermissions(prev => [...prev, data as Permission]);
      
      return true;
    } catch (error) {
      console.error('Error creating permission:', error);
      toast.error('Failed to create permission');
      return false;
    }
  };

  // Get default permissions for a role
  const getDefaultPermissions = (role: UserRole): Permission[] => {
    return FEATURES.map(feature => ({
      id: `default-${role}-${feature.id}`,
      role: role,
      feature_id: feature.id,
      can_access: feature.default_access[role]
    }));
  };

  return (
    <PermissionContext.Provider value={{
      userPermissions,
      hasPermission,
      loadingPermissions,
      refreshPermissions,
      updatePermission,
      createPermission,
      getDefaultPermissions
    }}>
      {children}
    </PermissionContext.Provider>
  );
}

// Custom hook to use the permission context
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}